/* ============================================
   ARLOURA — Interactive Motion System
   Mouse effects, scroll reveals, parallax,
   magnetic buttons, text animations
   ============================================ */

document.addEventListener('DOMContentLoaded', () => {
  // ── Smooth scroll for anchor links ──
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // ── Scroll Reveal Observer (Spring-powered when available) ──
  const useSpring = window.ArlouraSpring;

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (useSpring) {
          // Spring physics reveal
          const el = entry.target;
          const y = 40;
          el.style.opacity = '0';
          el.style.transform = `translateY(${y}px)`;
          el.style.willChange = 'opacity, transform';

          const animY = new useSpring.SpringAnimator({
            tension: 180, friction: 22, from: y, to: 0,
            onUpdate: (v) => { el.style.transform = `translateY(${v}px)`; }
          });
          const animO = new useSpring.SpringAnimator({
            tension: 180, friction: 22, from: 0, to: 1,
            onUpdate: (v) => { el.style.opacity = v; }
          });
          animY.start();
          animO.start();

          // Stagger children with spring
          const staggerChildren = entry.target.querySelectorAll('.arloura-stagger-child');
          if (staggerChildren.length > 0) {
            const stagger = new useSpring.SpringStagger({
              items: Array.from(staggerChildren),
              tension: 200, friction: 24, staggerDelay: 60,
              from: { opacity: 0, y: 20, scale: 0.95 },
              to: { opacity: 1, y: 0, scale: 1 }
            });
            stagger.animate();
          }
        } else {
          // CSS fallback
          entry.target.classList.add('arloura-revealed');
          const staggerChildren = entry.target.querySelectorAll('.arloura-stagger-child');
          staggerChildren.forEach((child, i) => {
            child.style.transitionDelay = `${i * 120}ms`;
            child.classList.add('arloura-stagger-revealed');
          });
        }
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -60px 0px'
  });

  // Observe all elements that want reveal animation
  document.querySelectorAll('.arloura-scroll-reveal, .arloura-scroll-reveal-left, .arloura-scroll-reveal-right, .arloura-scroll-reveal-up, .arloura-scroll-reveal-scale').forEach(el => {
    revealObserver.observe(el);
  });

  // Auto-reveal sections with cascading delays
  document.querySelectorAll('.shopify-section').forEach((section, i) => {
    section.classList.add('arloura-scroll-reveal');
    section.style.transitionDelay = `${i * 50}ms`;
    revealObserver.observe(section);
  });

  // ── Parallax on Scroll ──
  const parallaxElements = document.querySelectorAll('.arloura-parallax');
  let ticking = false;

  function updateParallax() {
    const scrollY = window.pageYOffset;
    parallaxElements.forEach(el => {
      const speed = parseFloat(el.dataset.speed) || 0.15;
      const rect = el.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const viewCenter = window.innerHeight / 2;
      const offset = (centerY - viewCenter) * speed;
      el.style.transform = `translate3d(0, ${offset}px, 0)`;
    });
    ticking = false;
  }

  // ── Mouse Parallax (for hero sections) ──
  const mouseParallaxContainers = document.querySelectorAll('.arloura-mouse-parallax');
  const mouseParallaxItems = [];

  mouseParallaxContainers.forEach(container => {
    const items = container.querySelectorAll('[data-mouse-depth]');
    items.forEach(item => {
      mouseParallaxItems.push({
        el: item,
        depth: parseFloat(item.dataset.mouseDepth) || 0.03,
        container: container
      });
    });
  });

  let mouseX = 0, mouseY = 0;
  let currentMouseX = 0, currentMouseY = 0;

  document.addEventListener('mousemove', (e) => {
    mouseX = (e.clientX / window.innerWidth - 0.5) * 2;
    mouseY = (e.clientY / window.innerHeight - 0.5) * 2;
  });

  function animateMouseParallax() {
    currentMouseX += (mouseX - currentMouseX) * 0.08;
    currentMouseY += (mouseY - currentMouseY) * 0.08;

    mouseParallaxItems.forEach(item => {
      const moveX = currentMouseX * item.depth * 100;
      const moveY = currentMouseY * item.depth * 100;
      item.el.style.transform = `translate3d(${moveX}px, ${moveY}px, 0)`;
    });

    requestAnimationFrame(animateMouseParallax);
  }

  if (mouseParallaxItems.length > 0) {
    animateMouseParallax();
  }

  // ── Magnetic Buttons (Spring-powered when available) ──
  document.querySelectorAll('.arloura-magnetic').forEach(btn => {
    if (useSpring) {
      new useSpring.SpringMagnetic(btn, {
        tension: 300, friction: 20,
        strength: parseFloat(btn.dataset.magneticStrength) || 0.3
      });
    } else {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        const strength = parseFloat(btn.dataset.magneticStrength) || 0.3;
        btn.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
        btn.style.transition = 'transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = 'translate(0, 0)';
        btn.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      });
    }
  });

  // ── Custom Cursor Trail ──
  const cursorTrail = document.createElement('div');
  cursorTrail.className = 'arloura-cursor-trail';
  document.body.appendChild(cursorTrail);

  let trailX = 0, trailY = 0;
  let targetTrailX = 0, targetTrailY = 0;
  let trailAnimX = null, trailAnimY = null;

  document.addEventListener('mousemove', (e) => {
    targetTrailX = e.clientX;
    targetTrailY = e.clientY;
    document.documentElement.style.setProperty('--mouse-x', e.clientX + 'px');
    document.documentElement.style.setProperty('--mouse-y', e.clientY + 'px');

    if (useSpring) {
      if (trailAnimX) trailAnimX.stop();
      if (trailAnimY) trailAnimY.stop();

      trailAnimX = new useSpring.SpringAnimator({
        tension: 120, friction: 18, from: trailX, to: targetTrailX - 20,
        onUpdate: (v) => { trailX = v; cursorTrail.style.transform = `translate(${trailX}px, ${trailY}px)`; }
      });
      trailAnimY = new useSpring.SpringAnimator({
        tension: 120, friction: 18, from: trailY, to: targetTrailY - 20,
        onUpdate: (v) => { trailY = v; cursorTrail.style.transform = `translate(${trailX}px, ${trailY}px)`; }
      });
      trailAnimX.start();
      trailAnimY.start();
    }
  });

  if (!useSpring) {
    function animateCursorTrail() {
      trailX += (targetTrailX - trailX) * 0.12;
      trailY += (targetTrailY - trailY) * 0.12;
      cursorTrail.style.transform = `translate(${trailX - 20}px, ${trailY - 20}px)`;
      requestAnimationFrame(animateCursorTrail);
    }
    animateCursorTrail();
  }

  // Expand cursor on interactive elements
  document.querySelectorAll('a, button, .card-wrapper, .arloura-magnetic').forEach(el => {
    el.addEventListener('mouseenter', () => cursorTrail.classList.add('arloura-cursor-expanded'));
    el.addEventListener('mouseleave', () => cursorTrail.classList.remove('arloura-cursor-expanded'));
  });

  // Hide on mobile
  if ('ontouchstart' in window) {
    cursorTrail.style.display = 'none';
  }

  // ── Text Split + Reveal Animation ──
  function splitTextIntoSpans(el) {
    const text = el.textContent;
    const words = text.split(' ');
    el.innerHTML = words.map(word => {
      const chars = word.split('').map(char => `<span class="arloura-char-wrap"><span class="arloura-char">${char}</span></span>`).join('');
      return `<span class="arloura-word">${chars}</span>`;
    }).join(' ');
  }

  document.querySelectorAll('.arloura-text-reveal').forEach(el => {
    splitTextIntoSpans(el);
    revealObserver.observe(el);
    // Add the class that triggers per-character animation
    el.classList.add('arloura-text-reveal-active');
  });

  // ── Counter Animation ──
  const counterObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const target = parseInt(entry.target.dataset.target) || 0;
        const suffix = entry.target.dataset.suffix || '';
        const prefix = entry.target.dataset.prefix || '';
        const duration = 2000;
        const startTime = performance.now();

        function updateCounter(currentTime) {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // Ease out cubic
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = Math.round(eased * target);
          entry.target.textContent = prefix + current.toLocaleString() + suffix;
          if (progress < 1) requestAnimationFrame(updateCounter);
        }
        requestAnimationFrame(updateCounter);
        counterObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  document.querySelectorAll('.arloura-counter').forEach(el => {
    counterObserver.observe(el);
  });

  // ── Section Reveal Line (decorative) ──
  document.querySelectorAll('.arloura-section-reveal-line').forEach(el => {
    const lineObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('arloura-line-revealed');
        }
      });
    }, { threshold: 0.3 });
    lineObserver.observe(el);
  });

  // ── Smooth Section Scroll Snap for Hero ──
  const heroSection = document.querySelector('.slideshow');
  if (heroSection) {
    let lastScrollTop = 0;
    const heroObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          document.body.classList.add('arloura-hero-visible');
        } else {
          document.body.classList.remove('arloura-hero-visible');
        }
      });
    }, { threshold: 0.5 });
    heroObserver.observe(heroSection);
  }

  // ── Global Scroll Handler ──
  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(updateParallax);
      ticking = true;
    }

    // Header shrink on scroll
    const header = document.querySelector('.section-header');
    if (header) {
      if (window.scrollY > 100) {
        header.classList.add('arloura-header-scrolled');
      } else {
        header.classList.remove('arloura-header-scrolled');
      }
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  // ── Image Reveal on Load ──
  document.querySelectorAll('.media img, .card__media img').forEach(img => {
    if (img.complete) {
      img.classList.add('arloura-img-loaded');
    } else {
      img.addEventListener('load', () => {
        img.classList.add('arloura-img-loaded');
      });
    }
  });

  // ── Mouse Glow on Cards (Spring hover when available) ──
  document.querySelectorAll('.card-wrapper').forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      card.style.setProperty('--mouse-x', (e.clientX - rect.left) + 'px');
      card.style.setProperty('--mouse-y', (e.clientY - rect.top) + 'px');
    });
    if (useSpring) {
      new useSpring.SpringHover(card, {
        tension: 200, friction: 20, scaleFrom: 1, scaleTo: 1.02
      });
    }
  });

  // ── Slideshow Parallax (Spring-powered when available) ──
  const slideshowSlide = document.querySelector('.slideshow__slide');
  if (slideshowSlide) {
    const slideshowImg = slideshowSlide.querySelector('.slideshow__media img, .slideshow__media video');
    if (slideshowImg) {
      if (useSpring) {
        let imgX = 0, imgY = 0;
        let animImgX = null, animImgY = null;
        document.addEventListener('mousemove', (e) => {
          const tx = (e.clientX / window.innerWidth - 0.5) * 15;
          const ty = (e.clientY / window.innerHeight - 0.5) * 10;
          if (animImgX) animImgX.stop();
          if (animImgY) animImgY.stop();
          animImgX = new useSpring.SpringAnimator({
            tension: 100, friction: 15, from: imgX, to: tx,
            onUpdate: (v) => { imgX = v; slideshowImg.style.transform = `scale(1.05) translate(${imgX}px, ${imgY}px)`; }
          });
          animImgY = new useSpring.SpringAnimator({
            tension: 100, friction: 15, from: imgY, to: ty,
            onUpdate: (v) => { imgY = v; slideshowImg.style.transform = `scale(1.05) translate(${imgX}px, ${imgY}px)`; }
          });
          animImgX.start();
          animImgY.start();
        });
      } else {
        slideshowImg.style.transition = 'transform 0.1s linear';
        document.addEventListener('mousemove', (e) => {
          const x = (e.clientX / window.innerWidth - 0.5) * 15;
          const y = (e.clientY / window.innerHeight - 0.5) * 10;
          slideshowImg.style.transform = `scale(1.05) translate(${x}px, ${y}px)`;
        });
      }
    }
  }

  // ── Auto-link email addresses ──
  const emailRegex = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g;
  const emailContainers = document.querySelectorAll('.rte, .footer-block__details-content, .footer__copyright, .collection-hero__description, .product__text, .product__description, .image-with-text__text, .rich-text__text, address');

  emailContainers.forEach(container => {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.parentElement && node.parentElement.tagName !== 'A' && node.parentElement.tagName !== 'SCRIPT') {
        textNodes.push(node);
      }
    }

    textNodes.forEach(node => {
      const text = node.textContent;
      if (emailRegex.test(text)) {
        emailRegex.lastIndex = 0;
        const linked = text.replace(emailRegex, (match) => {
          return `<a href="mailto:${match}" class="arloura-email-link">${match}</a>`;
        });
        if (linked !== text) {
          const span = document.createElement('span');
          span.innerHTML = linked;
          node.parentNode.replaceChild(span, node);
        }
      }
    });
  });

  // ── Auto-link phone numbers ──
  const phoneRegex = /(?:\+?\d{1,3}[-.\s]?)?(?:\(?\d{2,4}\)?[-.\s]?)?\d{3,4}[-.\s]?\d{3,4}/g;
  const phoneContainers = document.querySelectorAll('.rte, .footer-block__details-content, .footer__copyright, .collection-hero__description, .product__text, .product__description, .image-with-text__text, .rich-text__text, address');

  phoneContainers.forEach(container => {
    // Only process text nodes, skip existing links
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.parentElement && node.parentElement.tagName !== 'A' && node.parentElement.tagName !== 'SCRIPT') {
        textNodes.push(node);
      }
    }

    textNodes.forEach(node => {
      const text = node.textContent;
      if (phoneRegex.test(text)) {
        phoneRegex.lastIndex = 0;
        const linked = text.replace(phoneRegex, (match) => {
          const cleaned = match.replace(/[^\d+]/g, '');
          if (cleaned.length >= 7) {
            return `<a href="tel:${cleaned}" class="arloura-phone-link">${match}</a>`;
          }
          return match;
        });
        if (linked !== text) {
          const span = document.createElement('span');
          span.innerHTML = linked;
          node.parentNode.replaceChild(span, node);
        }
      }
    });
  });

  // ── Smooth page load animation ──
  document.body.classList.add('arloura-page-loaded');
});

/* ── Page Transition Overlay ── */
document.querySelectorAll('a[href]').forEach(link => {
  link.addEventListener('click', function(e) {
    const href = this.getAttribute('href');
    // Skip external links, anchors, mailto, tel, javascript
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:') || href.startsWith('http') || href.includes('cdn.shopify.com')) return;

    e.preventDefault();
    const overlay = document.createElement('div');
    overlay.className = 'arloura-page-transition';
    overlay.innerHTML = '<div class="arloura-transition-curve"></div>';
    document.body.appendChild(overlay);

    requestAnimationFrame(() => {
      overlay.classList.add('arloura-transition-active');
    });

    setTimeout(() => {
      window.location.href = href;
    }, 500);
  });
});
